import { Box, Drawer, Grid, Skeleton, Stack, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import Badge from '../badge'
import type { ResponseBadge } from '@/types/super-chain'
import badgesService from '@/features/superChain/services/badges.service'
import { type Address } from 'viem'
import useSafeInfo from '@/hooks/useSafeInfo'
import BadgeInfo from '../badgeInfo'
import css from './styles.module.css'
import useLocalStorage from '@/services/local-storage/useLocalStorage'

type Params = {
  id: number
  account: string
  isFavorite: boolean
}

function BadgesContent({
  badges,
  isLoading,
  error,
}: {
  badges?: ResponseBadge[]
  isLoading: boolean
  error: Error | null
}) {
  const { safeAddress, safeLoaded } = useSafeInfo()
  const [currentBadge, setCurrentBadge] = useState<(ResponseBadge & { isFavorite: boolean }) | null>(null)
  const [favoriteBadgesLocalStorage, setFavoriteBadgesLocalStorage] = useLocalStorage<string>('favoriteBadges')
  const favoriteBadges = useMemo(
    () => (safeLoaded ? badgesService.getFavoriteBadges(safeAddress as Address) : []),
    [safeAddress, safeLoaded, favoriteBadgesLocalStorage],
  )

  if (isLoading)
    return (
      <Grid container item spacing={1}>
        <Grid xs={12} item>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Skeleton className={css.badgeSkeleton} variant="rectangular" width={245} height={324} />
            <Skeleton className={css.badgeSkeleton} variant="rectangular" width={245} height={324} />
            <Skeleton className={css.badgeSkeleton} variant="rectangular" width={245} height={324} />
            <Skeleton className={css.badgeSkeleton} variant="rectangular" width={245} height={324} />
          </Stack>
        </Grid>
      </Grid>
    )
  if (error)
    return (
      <Grid container item spacing={1}>
        <Grid xs={12} item>
          <Typography>Error: {error.message}</Typography>
        </Grid>
      </Grid>
    )

  if (!badges)
    return (
      <Grid container item spacing={1}>
        <Grid xs={12} item>
          <Typography>No badges founded</Typography>
        </Grid>
      </Grid>
    )

  return (
    <Grid container item spacing={1}>
      <Grid xs={12} item sx={{ mt: '10px' }}>
        <Box
          sx={{
            display: 'grid',

            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '16px',
            width: '100%',
            '& > *': {
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {badges
            .sort((a, b) => {
              const aIsFavorite = favoriteBadges.includes(a.badgeId)
              const bIsFavorite = favoriteBadges.includes(b.badgeId)
              const aIsCompleted = Number(a.tier) === a.badgeTiers.length
              const bIsCompleted = Number(b.tier) === b.badgeTiers.length

              // Primero ordenar por favoritos
              if (aIsFavorite !== bIsFavorite) {
                return aIsFavorite ? -1 : 1
              }

              // Luego por completadas
              if (aIsCompleted !== bIsCompleted) {
                return aIsCompleted ? 1 : -1
              }

              return 0
            })
            .map((badge) => (
              <Badge
                data={badge}
                key={badge.badgeId}
                switchFavorite={({ id, account, isFavorite }: { id: number; account: Address; isFavorite: boolean }) =>
                  badgesService.switchFavoriteBadge(id, account, isFavorite, setFavoriteBadgesLocalStorage)
                }
                setCurrentBadge={setCurrentBadge}
                isFavorite={favoriteBadges.includes(badge.badgeId)}
              />
            ))}
        </Box>
      </Grid>
      <Drawer
        variant="temporary"
        anchor="right"
        onClose={() => setCurrentBadge(null)}
        open={!!currentBadge}
        PaperProps={{
          sx: {
            width: 340,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
          },
        }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <BadgeInfo
            switchFavorite={({ id, account, isFavorite }: { id: number; account: Address; isFavorite: boolean }) =>
              badgesService.switchFavoriteBadge(id, account, isFavorite, setFavoriteBadgesLocalStorage)
            }
            setCurrentBadge={setCurrentBadge}
            currentBadge={currentBadge}
          />
        </Box>
      </Drawer>
    </Grid>
  )
}

export default BadgesContent
